<template>
  <div
    class="table-cell"
    :class="[props.headerCell.name,
             { 'active-only-one': tableStore.activeCellOnlyOne === `${props.rowIndex}-${cellIndex}` },
             { active: tableStore.activeCells.has(`${props.rowIndex}-${cellIndex}`) },
             { disabled: tableStore.ctrlIsPressed || tableStore.requestIsExecution },
             { deleted: row.is_deleted },
             { failed: row.is_failed },
             { readonly: props.readonly }
    ]"
    :style="{ width: props.headerCell.width + 'px'}"
    @mousedown="mousedownHandler($event)"
  >
    <!--  Отображение чек-бокса/ошибок для первого столбца  -->
    <template v-if="props.cellIndex === 0">
      <Checkbox
        v-model="rowInFilteredData.checked"
        :name="`row-${rowIndex}`"
      />
      <div
        v-if="row.status === 'error'"
        class="table-cell__icon"
      >
        <ErrorRowIcon />
      </div>
    </template>

    <!--  Отображение кнопки редактирования источника для последнего столбца  -->
    <template v-if="props.cellIndex === props.cellCount">
      <div
        class="edit-row-button"
        @click="goStatsPage(row.id)"
      >
        <StatsIcon />
      </div>
      <div
        class="edit-row-button edit-row-button--n2"
        @click="emit('edit-row')"
      >
        <PencilIcon />
      </div>
    </template>

    <!--  Вывод текста  -->
    <template v-if="headerCell.type === 'text'">
      <div class="table-cell__text">
        {{ row[headerCell.name] }}
      </div>
    </template>

    <!--  Вывод переключателя  -->
    <template v-else-if="headerCell.type === 'checkbox'">
      <div class="table-cell__text">
        <SwitchBase
          v-model="rowInFilteredData.is_enabled"
          @change="tableStore.toggleIsEnabledFeeds(rowInFilteredData)"
        />
      </div>
    </template>

    <!--  Вывод выпадающего списка  -->
    <template v-else-if="headerCell.type === 'dropdown'">
      <CellDropdown
        :cell="row[headerCell.name]"
        :items="templates"
        @save-dropdown-cell="args => emit('update-row', {id: row.id, name: headerCell.name, value: args})"
      />
    </template>

    <!--  Вывод форматированной строки из cron quartz  -->
    <template v-else-if="headerCell.type === 'cron'">
      <div class="table-cell__text">
        {{ formateCronString(row[headerCell.name]) }}
      </div>
    </template>

    <!--  Вывод списка  -->
    <template v-else-if="headerCell.type === 'list'">
      <div class="table-cell__list scroll-thin">
        <GlobusBadge
          v-for="(tag) of row[headerCell.name]"
          :key="tag.id"
          :use-cross-icon="false"
        >
          {{ tagText(tag) }}
        </GlobusBadge>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, defineProps, toRaw } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTableStore } from "@/stores/table.js";
import ErrorRowIcon from "@/components/icons/ErrorRowIcon.vue";
import SwitchBase from "@/components/UI/SwitchBase.vue";
import {templates} from "@/exampleData/tableData";
import CellDropdown from "@/components/table/CellDropdown.vue";
import {formateCronString} from "@/composables/frequencyData";
import GlobusBadge from "@/components/UI/GlobusBadge.vue";
import PencilIcon from "@/components/icons/PencilIcon.vue";
import StatsIcon from "@/components/icons/StatsIcon.vue";

const router = useRouter();

const props = defineProps({
  headerCell: {
    type: Object,
    required: true
  },
  row: {
    type: Object,
    required: true
  },
  rowIndex: {
    type: Number,
    required: true
  },
  cellIndex: {
    type: Number,
    required: true
  },
  readonly: {
    type: Boolean,
    required: true
  },
  cellCount: {
    type: Number,
    required: true
  }
});

const emit = defineEmits(["edit-row", "update-row"]);

const tableStore = useTableStore();

const rowIndexCellIndex = computed(() => {
  return `${props.rowIndex}-${props.cellIndex}`;
});

const tagText = (tag) => {
  return tag.category ? `${tag.category} | ${tag.name}` : tag.name;
};

// Вспомогательная функция для добычи значения тега по его категории
const getTagName = (category) => {
  return toRaw(props.row.tags).find((tag) => tag.category === category)?.name || "";
};

const rowInFilteredData = computed(() => {
  const fData = tableStore.filteredListData[props.rowIndex];
  fData.region = getTagName("Регион");
  fData.subRegion = getTagName("Субрегион");
  fData.country = getTagName("Страна");
  return fData;
});

const mousedownHandler = () => {
  if(props.readonly) return;
  if(!tableStore.activeCells.has(rowIndexCellIndex.value)) {
    tableStore.activeCells.add(rowIndexCellIndex.value);
    tableStore.startedActiveCell = rowIndexCellIndex.value;
  } else {
    if(tableStore.startedActiveCell !== rowIndexCellIndex.value || tableStore.startedActiveCell.size === 1) {
      tableStore.activeCells.delete(rowIndexCellIndex.value);
    }
  }
};

const goStatsPage = (sourceId) => {
  // Комментарий к задаче 2093. Если использовать router.push, то не выделяется активный пункт меню
  window.location.href = router.resolve({ name: "analytics", query: { sourceId } }).href;
};
</script>


<style lang="scss" scoped>
.table {
  &-cell {
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid transparent;
    transition: background .2s;
    padding: 16px 24px;
    background-color: #FFFFFF;

    &__icon {
      width: 24px;
      height: 24px;
    }

    &__text {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;

    }

    &__list {
      display: flex;
      align-items: center;
      gap: 5px;
      padding-bottom: 4px;
      overflow-y: hidden;
      overflow-x: auto;
    }

    &:hover {
      background: #F7F8FD;
    }

    &:first-child {
      padding-left: 25px;
    }

    &:not(:last-child) {
      border-right: 1px solid var(--button-secondary-hower);
    }
  }
}

.edit-row-button {
  align-items: center;
  background: #7B87A5;
  border-radius: 50px;
  cursor: pointer;
  display: flex;
  height: 38px;
  justify-content: center;
  position: absolute;
  left: calc(100vw - 99px);
  user-select: none;
  width: 38px;

  opacity: 0;
  transition: opacity .2s;
  pointer-events: none;

  &--n2 {
    left: calc(100vw - 149px);
  }

  &:hover {
    background: #909AB3;
  }
}
</style>